import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import {
  createLink, listLinks, retireLink, updateLink, type Link, type LinkCreateInput,
} from '../../services/linksService';
import type { OutreachCard } from '../../services/dataService';
import TrackedLinkForm from '../links/TrackedLinkForm';
import LinkRow, { CLICKS_HONESTY } from '../links/LinkRow';

/**
 * Your sponsor links — every Bearri link the creator has made, on the
 * dashboard beside their sponsors. A link forwards to the brand's exact URL
 * and counts clicks anonymously; the sheet says plainly what that does and
 * does not measure, and never shows a number it did not count.
 */
export interface SponsorLinksViewProps {
  /** null while the list loads. */
  links: Link[] | null;
  error?: string | null;
  /** Deals a new link can be attached to (the Deals pipeline). */
  deals?: Array<{ id: string; brand_name: string }>;
  onCreate: (input: LinkCreateInput) => Promise<unknown>;
  onToggleActive: (link: Link) => Promise<void>;
  onRetire: (link: Link) => Promise<void>;
  onRetry?: () => void;
}

export const SponsorLinksView: React.FC<SponsorLinksViewProps> = ({
  links, error = null, deals, onCreate, onToggleActive, onRetire, onRetry,
}) => {
  const [adding, setAdding] = useState(false);
  const form = (
    <div className="br-sheet br-sheet--plain mt-3 p-3 sm:p-4">
      <TrackedLinkForm idPrefix="dash-link" deals={deals}
        onCreate={async (input) => { await onCreate(input); setAdding(false); }} onCancel={() => setAdding(false)} />
    </div>
  );

  let body: React.ReactNode;
  if (error) {
    body = (
      <div role="alert" className="br-note br-note--amber br-note--straight br-note--wide" style={{ marginBottom: 0 }}>
        <p className="min-w-0 flex-1">{error}</p>
        {onRetry && <button type="button" onClick={onRetry} className="br-inkline text-caption">Try again</button>}
      </div>
    );
  } else if (links === null) {
    body = <div className="br-ruled-empty br-ruled-empty--loading" role="status">Opening your links…</div>;
  } else if (links.length === 0) {
    body = (
      <>
        <div className="br-ruled-empty">
          No Bearri links yet. A Bearri link forwards to a sponsor’s exact URL and counts clicks — how many, from which
          kind of device and site, never who clicked and never how many bought.
          {!adding && <button type="button" onClick={() => setAdding(true)} className="br-slab br-slab--blue">Make a link</button>}
        </div>
        {adding && form}
      </>
    );
  } else {
    body = (
      <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="br-caption">{CLICKS_HONESTY}</p>
          {!adding && <button type="button" onClick={() => setAdding(true)} className="br-slab br-slab--blue br-slab--sm">New link</button>}
        </div>
        {adding && form}
        <div className="br-ruled mt-1">
          {links.map((l) => <LinkRow key={l.id} link={l} showDeal onToggleActive={onToggleActive} onRetire={onRetire} />)}
        </div>
      </>
    );
  }
  return <div className="br-sheet p-4 sm:p-5">{body}</div>;
};

/** The live list: the signed-in creator's links, read from `/api/links`. */
const SponsorLinks: React.FC<{ pipeline?: OutreachCard[] | null }> = ({ pipeline }) => {
  const { getToken } = useAuth();
  const [links, setLinks] = useState<Link[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLinks(null);
    try {
      const token = (await getToken()) || '';
      setLinks((await listLinks(token)).links);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your links.');
    }
  }, [getToken]);

  // Runs once per mount (getToken is stable); a failure shows a retry and never re-runs itself.
  useEffect(() => { void load(); }, [load]);

  const deals = useMemo(
    () => (pipeline || []).map((c) => ({ id: c.id, brand_name: c.brand_name })),
    [pipeline],
  );

  const onCreate = async (input: LinkCreateInput) => {
    const token = (await getToken()) || '';
    const created = await createLink(token, input);
    setLinks((ls) => [created, ...(ls || [])]);
    toast.success('Bearri link made — copy it from the list.');
  };
  const onToggleActive = async (link: Link) => {
    try {
      const updated = await updateLink((await getToken()) || '', link.id, { active: !link.active });
      setLinks((ls) => (ls || []).map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That did not save — try again.');
    }
  };
  const onRetire = async (link: Link) => {
    try {
      await retireLink((await getToken()) || '', link.id);
      setLinks((ls) => (ls || []).filter((x) => x.id !== link.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove the link.');
    }
  };

  return (
    <SponsorLinksView links={links} error={error} deals={deals} onCreate={onCreate}
      onToggleActive={onToggleActive} onRetire={onRetire} onRetry={() => { void load(); }} />
  );
};

export default SponsorLinks;
